import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { ValidEventTypes, EventTypeEnum, getDateEventByType } from '../standard-suggest/eventTypes'
import { defaultYearRangeEstimate } from './defaultYearRange'
import StandardSuggestNEXT from '../standard-suggest/StandardSuggestNEXT'
import fetchDateSuggestions from './fetchDateSuggestions'

export function getDateSuggestions(searchTerm, config = {}) {
  const { yearRangeEstimate = defaultYearRangeEstimate } = config
  return fetchDateSuggestions(searchTerm, yearRangeEstimate)
}

/**
 * DateSuggestNEXT
 *
 * A component for suggesting and selecting dates, leveraging StandardSuggestNEXT for core functionality.
 * Handles date-specific suggestion fetching with optional year range filtering.
 */
const DateSuggestNEXT = forwardRef(function DateSuggest(
  {
    label,
    standardize, // Accept but don't pass through to avoid DOM warning
    eventType = EventTypeEnum.NONE,
    yearRangeEstimate = defaultYearRangeEstimate,
    config,
    getSuggestions,
    ...props
  },
  ref
) {
  let inputLabel
  if (label === null) {
    inputLabel = null
  } else if (typeof label === 'string') {
    inputLabel = label
  } else {
    inputLabel = getDateEventByType(eventType)?.label
  }

  // Use custom getSuggestions if provided, otherwise use the default implementation
  const suggestionsHandler = getSuggestions || ((searchTerm) => getDateSuggestions(searchTerm, { yearRangeEstimate }))

  return (
    <StandardSuggestNEXT
      label={inputLabel}
      type="date"
      config={config}
      getSuggestions={suggestionsHandler}
      {...props}
      ref={ref}
    />
  )
})

export default DateSuggestNEXT
export const meta = {
  name: 'DateSuggestNEXT',
  isZionFormComponent: true,
}

DateSuggestNEXT.meta = meta

// Shape of the standardized date object from the API
const dateStandardShape = PropTypes.shape({
  standardizedText: PropTypes.string,
  originalText: PropTypes.string,
  earliestAstro: PropTypes.number,
  latestAstro: PropTypes.number,
  yearRange: PropTypes.string,
  formalText: PropTypes.string.isRequired,
})

// Shape of the config object from useStandardSuggestNEXT
const configShape = PropTypes.shape({
  userInput: PropTypes.string.isRequired,
  fieldValue: PropTypes.string.isRequired,
  selectedItem: PropTypes.shape({
    primaryText: PropTypes.string.isRequired,
    isStandard: PropTypes.bool,
    standard: dateStandardShape,
  }),
  standardSuggestSuggestion: PropTypes.shape({
    originalText: PropTypes.string,
    standard: dateStandardShape,
  }),
  set: PropTypes.func.isRequired,
  standardize: PropTypes.bool,
})

DateSuggestNEXT.propTypes = {
  /** The label associated with the Autosuggest input TextField. Pass null to hide the label. */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  /** The event type */
  eventType: PropTypes.oneOf(ValidEventTypes),
  /** Year range estimate for filtering suggestions */
  yearRangeEstimate: PropTypes.shape({
    startYear: PropTypes.number,
    endYear: PropTypes.number,
  }),
  /** Configuration object from useStandardSuggestNEXT */
  config: configShape.isRequired,
  /** Custom suggestions function */
  getSuggestions: PropTypes.func,
}

export { DateSuggestNEXT }
